import { Component, OnInit, Input, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { FacilityService } from '../../facility/facility.service';
import { LOV_KEYS } from 'src/app/dataModels/lovs';
import { CONSTANTS, MODAL_DIALOG_STATUS, DIALOG_MSGS } from 'src/app/dataModels/constants';
import { ResourceManagerService } from 'src/app/services/resourceManager/resource-manager.service';
import { HelperService } from 'src/app/services/utils/helper.service';
import { UntypedFormGroup, UntypedFormControl, Validators, AbstractControl } from '@angular/forms';
import { Regex } from 'src/app/dataModels/regex';
import { RequiredValidator } from 'src/app/customValidators/required/required';
import { DateValidators } from 'src/app/customValidators/date/date-validators';
import { CanComponentDeactivate } from 'src/app/can-deactivate.guard';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { VaccineOrderingService } from '../../vaccine-ordering/vaccine-ordering.service';
/**
 * Audit Memo for Facility
 */
@Component({
  selector: 'app-memo',
  templateUrl: './memo.component.html',
  styleUrls: ['./memo.component.scss']
})
/**
 * Memo component
 */
export class MemoComponent implements OnInit, CanComponentDeactivate, OnChanges {

  /**
   * Retrieve input field
   */
  @ViewChild('auditInput') auditInput: ElementRef<HTMLInputElement>;

  /**
   * Facility Id
   */
  @Input() facilityId;

  /**
   * Facility IdSeq
   */
  @Input() facilityIdSeq;

  /**
   * VFC Pin
   */
  @Input() vfcPin;

  /**
   * date of facilityCreated
   */
  @Input() facilityDateCreated;

  /**
   * List of notes
   */
  noteList = [];

  /**
   * List of headers for notes
   */
  headers = [];

  /**
   * List of profiles
   */
  profileList = [];

  /**
   * Constants for SUPER ADMIN PROFILE
   */
  SUPER_ADMIN_PROFILE = 'SUPER ADMIN';

  /**
   * boolean indicator to indicate form is submitted, and to run validations
   */
  isMemoSubmit = false;

  /**
   * Form group
   */
  memoFrm: UntypedFormGroup;

  /**
   * List of audit Type
   */
  auditTypeList = [];

  /**
   * List of VFC Pin
   */
  vfcList = [];

  /**
   * Indicator to display busy loading
   */
  isBusy;

  /**
   * indicate if Add Audit Review is allowed
   */
  @Input() isAddAllowed = true;

  /**
   * indicate if Update Audit Review is allowed
   */
  @Input() isUpdateAllowed = true;

  /**
   * Reference of element
   */
  @ViewChild('memo') memo: ElementRef;

  /**
   * Constructor to inject dependency
   * @param facilityService instance of FacilityService
   * @param resourceManagerService instance of resourceManagerService
   * @param helperService instance of HelperService
   */
  constructor(
    private facilityService: FacilityService,
    private resourceManagerService: ResourceManagerService,
    private helperService: HelperService,
    private vaccingOrderService : VaccineOrderingService
  ) {}

  /**
   * initialize component
   */
  ngOnInit(): void {
    this.initFormControls();
    this.getLovs();
    if (!this.helperService.IsNullOrEmpty(this.vfcPin)) {
      this.addVFCPinToList(this.vfcPin);
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.auditInput) {
        this.auditInput.nativeElement.focus();
      }
    }, 500);
  }

  /**
   * initialize form controls
   */
  initFormControls() {
    this.memoFrm = new UntypedFormGroup({
      auditBy: new UntypedFormControl('', [
        Validators.required,
        RequiredValidator.validateRequired,
        Validators.minLength(2),
        Validators.maxLength(60),
        Validators.pattern(Regex.ALPHA)
      ]),
      auditType: new UntypedFormControl('', Validators.required),
      auditDate: new UntypedFormControl('', [
        Validators.required,
        DateValidators.isDateLessThanMinimum,
        DateValidators.isDateMoreThanToday,
        DateValidators.isValidDate,
        DateValidators.dateLessThanMinDate(this.facilityDateCreated, 'facility created date')
      ]),
      auditVisibleTo: new UntypedFormControl(''),
      vfcPin: new UntypedFormControl(''),
      comments: new UntypedFormControl('', [Validators.required, RequiredValidator.validateRequired, Validators.maxLength(1000)]),
      facilityDateCreated: new UntypedFormControl(this.facilityDateCreated)
    });
  }

  /**
   * getter for form controls
   */
  get f() {
    return this.memoFrm?.controls;
  }

  /**
   * Check if control is invalid
   * @param ctrl ctrl to check invalidity for
   */
  isControlInvalid(ctrl: AbstractControl) {
    return this.helperService.isControlInvalid(this.isMemoSubmit, ctrl);
  }

  /**
   * initialize component - lazy loaded
   */
  initComponent() {
    this.memo?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.headers = this.facilityService.getNoteHeader(this.isUpdateAllowed);
    this.getFacilityNote();
    this.getLovs();
  }

  /**
   * get notes for facilityId
   */
  getFacilityNote() {
    this.isBusy = this.vaccingOrderService.getFacilityNote(this.facilityId).then(response => {
      if (!response.isError) {
        this.noteList = response.data;
      }
    });
  }

  /**
   * get LOVs
   * 1. LOV_KEYS.PROFILE_TYPE
   * 2. LOV_KEYS.NOTE_TYPE
   */
  getLovs() {
    const resourceList = [
      {
        key: LOV_KEYS.PROFILE_TYPE,
        type: CONSTANTS.LOV
      },
      {
        key: LOV_KEYS.NOTE_TYPE,
        type: CONSTANTS.LOV
      }
    ];

    this.resourceManagerService.getResourcesCoreApp(resourceList).then(response => {
      this.profileList = response[LOV_KEYS.PROFILE_TYPE];
      this.auditTypeList = response[LOV_KEYS.NOTE_TYPE];
      console.log(this.auditTypeList, 'auditTypeList');
      if (this.profileList) {
        this.profileList = this.profileList.filter(item => {
          return item.descr.toUpperCase() !== this.SUPER_ADMIN_PROFILE;
        });
      }
    });
  }

  /**
   * Add VFC Pin of the facility
   */
  addVFCPinToList(vfcPin) {
    this.vfcList.push({ descr: vfcPin, columnValue: vfcPin });
  }

  /**
   * create memo
   */
  createMemo() {
    this.isMemoSubmit = true;
    if (this.memoFrm.valid) {
      this.isBusy = this.facilityService.createAuditNote(this.memoFrm.value, this.facilityId, true).then(response => {
        this.handleReponse(response, DIALOG_MSGS.NOTE_ADDED_SUCCESS);
        this.resetMemo();
      });
    }
  }

  /**
   * reset memo to its original state
   */
  resetMemo() {
    this.isMemoSubmit = false;
    this.helperService.resetForm(this.memoFrm);
    
    if (this.auditInput) {
      this.auditInput.nativeElement.focus();
    }
  }

  /**
   * update memo
   * @param event row being edited
   */
  updateMemo(event) {
    const row = event.row;
    const col = event.column;
    if (col === CONSTANTS.UPDATE) {
      const noteObj = this.noteList.find(note => note.serialNum === row.serialNum);

      if (noteObj.origVisibilityToList && row.visibilityToList) {
        let isFound = false;
        let isModified = false;
        if (noteObj.origVisibilityToList.length !== row.visibilityToList.length) {
          isModified = true;
        } else {
          for (const oldVisibility of noteObj.origVisibilityToList) {
            isFound = false;
            for (const newVisibility of row.visibilityToList) {
              if (oldVisibility.columnValue === newVisibility.columnValue) {
                isFound = true;
                break;
              }
            }
            if (!isFound) {
              isModified = true;
              break;
            }
          }
        }
        if (!isModified) {
          //this.helperService.showInfoMessage(CONSTANTS.NO_CHANGES_MADE_MSG, MODAL_DIALOG_STATUS.INFO);
          this.helperService.showInfoMessage(CONSTANTS.NO_CHANGES_MADE_MSG, MODAL_DIALOG_STATUS.INFO).subscribe(() => {
            setTimeout(() => {
              this.auditInput.nativeElement.focus();
            }, 200);
          });
        } else {
          this.isBusy = this.facilityService.updateAuditNote(row, this.facilityId , true).then(response => {
            this.handleReponse(response, DIALOG_MSGS.NOTE_UPDATED_SUCCESS);
          });
        }
      }
    }
  }

  /**
   * display success or error message and refresh note list on success
   */
  handleReponse(response, message) {
    if (response.status === CONSTANTS.SUCCESS || response.status === CONSTANTS.TRN_WARNING) {
      this.helperService.showSuccessMessage(message, MODAL_DIALOG_STATUS.SUCCESS).subscribe(() => {
        this.getFacilityNote();
        setTimeout(() => {
          this.auditInput.nativeElement.focus();
        }, 500);
      });
    } else {
      //this.helperService.showErrorMessage(response.message, MODAL_DIALOG_STATUS.ERROR);
      this.helperService.showErrorMessage(response.message, MODAL_DIALOG_STATUS.ERROR).subscribe(() => {
        setTimeout(() => {
          if (this.auditInput) {
            this.auditInput.nativeElement.focus();
          }
        }, 500);
      });
    }
  }

  /**
   * decide if component candeactivate
   */
  canDeactivate() {
    return this.helperService.canDeactivate(this.memoFrm);
  }

  /**
   * handle on changes for @Input() properties
   * @param changes changes event indicating values modified for @Input() properties
   */
  ngOnChanges(changes: SimpleChanges) {
    this.headers = this.facilityService.getNoteHeader(this.isUpdateAllowed);
  }
}
